import { useEffect, useState } from 'react';

import { api } from '../../api';
import { go } from '../../App';
import { useLoad } from '../../data';
import { Loading, PageHead, Panel, fmtMoney } from '../../ui';
import { Product } from '../../types';

// The supplier's OWN products (canon 11 E2 step 4 "First product", and the
// "Add your other trips" task afterwards). The supplier writes their own fields
// through POST/PUT /v1/products — gross price, no margin (DECISION-015/016);
// Eva's upsert_product is the SAME operation. This is a supplier-scoped route
// reusing the Form template (720 centred, sticky footer) — not a new registered
// rail screen (the register stays closed at 41; George's S11 ruling).

const KINDS: { key: 'experience' | 'transfer'; label: string }[] = [
  { key: 'experience', label: 'Experience' },
  { key: 'transfer', label: 'Transfer' },
];

export function Trips({ editId }: { editId?: string }) {
  const { data, error, reload } = useLoad<{ items: Product[] }>('/products');
  const items = data ? data.items : null;
  if (editId) return <Editor items={items} loadError={error} editId={editId} onSaved={reload} />;
  return <List items={items} loadError={error} />;
}

function List({ items, loadError }: { items: Product[] | null; loadError: string }) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title="My trips" sub="What you sell — the hotels you connect with book these." />

      {loadError && <p className="error-line">{loadError}</p>}
      {items === null ? (
        <Loading />
      ) : (
        <Panel title="Your trips" meta={items.length ? `${items.length}` : ''} flush>
          {items.length === 0 ? (
            <div className="card-body">
              <div className="empty-neutral">No trips yet — add your first so hotels can book you.</div>
            </div>
          ) : (
            <table>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="click" onClick={() => go(`/trips/${p.id}`)}>
                    <td>
                      {p.name}
                      <br />
                      <span className="hint">
                        {p.kind === 'transfer' ? 'Transfer' : 'Experience'}
                        {p.durationHours ? ` · ${p.durationHours} h` : ''}
                        {p.maxGuests ? ` · max ${p.maxGuests}` : ''}
                      </span>
                    </td>
                    <td className="num mono">
                      {fmtMoney(p.supplierPrice)}
                      <br />
                      <span className="hint">per {p.per}</span>
                    </td>
                    <td className="num"><span className="open">Edit →</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      <div className="form-foot">
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={() => go('/trips/new')} style={{ flex: 'none', minWidth: 160 }}>
          Add a trip
        </button>
      </div>
    </div>
  );
}

function Editor({
  items,
  loadError,
  editId,
  onSaved,
}: {
  items: Product[] | null;
  loadError: string;
  editId: string;
  onSaved: () => Promise<void>;
}) {
  const isNew = editId === 'new';
  const existing = isNew ? null : items?.find((p) => p.id === editId) ?? null;

  const [name, setName] = useState('');
  const [kind, setKind] = useState<'experience' | 'transfer'>('experience');
  const [per, setPer] = useState<'guest' | 'job'>('guest');
  const [priceStr, setPriceStr] = useState('');
  const [durationStr, setDurationStr] = useState('');
  const [maxStr, setMaxStr] = useState('');
  const [seeded, setSeeded] = useState(isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Seed the form from the existing product once it has loaded.
  useEffect(() => {
    if (!seeded && existing) {
      setName(existing.name);
      setKind(existing.kind);
      setPer(existing.per);
      setPriceStr((existing.supplierPrice.minor / 100).toFixed(2));
      setDurationStr(existing.durationHours != null ? String(existing.durationHours) : '');
      setMaxStr(existing.maxGuests != null ? String(existing.maxGuests) : '');
      setSeeded(true);
    }
  }, [existing, seeded]);

  const back = () => go('/trips');
  const priceMajor = Number.parseFloat(priceStr);
  const priceMinor = Number.isFinite(priceMajor) ? Math.round(priceMajor * 100) : NaN;
  const priceOk = Number.isInteger(priceMinor) && priceMinor >= 0;
  const nameOk = name.trim().length > 0 && name.trim().length <= 120;
  const canSave = nameOk && priceOk;

  const save = async () => {
    if (!canSave) {
      setErr(!nameOk ? 'A name is needed.' : 'The price must be a number, zero or more.');
      return;
    }
    setBusy(true);
    setErr('');
    const body: Record<string, unknown> = {
      name: name.trim(),
      kind,
      per,
      supplierPrice: { minor: priceMinor, currency: existing?.supplierPrice.currency ?? 'EUR' },
    };
    const dur = Number.parseFloat(durationStr);
    if (durationStr && Number.isFinite(dur)) body.durationHours = dur;
    const mx = Number.parseInt(maxStr, 10);
    if (maxStr && Number.isInteger(mx)) body.maxGuests = mx;
    try {
      if (isNew) await api('POST', '/products', body);
      else await api('PUT', `/products/${editId}`, body);
      await onSaved();
      back();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };

  if (!isNew && items === null) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <PageHead title="Edit trip" />
        {loadError ? <p className="error-line">{loadError}</p> : <Loading />}
      </div>
    );
  }
  if (!isNew && !existing) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <PageHead title="Edit trip" />
        <div className="empty-neutral">No such trip. <a href="#/trips">Back to my trips</a></div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <PageHead title={isNew ? 'Add a trip' : 'Edit trip'} sub="Your figure is gross — the hotel adds its own margin on top." />

      <Panel title="The trip">
        <div className="field">
          <label htmlFor="tname">Name</label>
          <input id="tname" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label>Kind</label>
          <div>
            {KINDS.map((k) => (
              <button key={k.key} type="button" className={`reason ${kind === k.key ? 'selected' : ''}`} onClick={() => setKind(k.key)}>
                {k.label}
                {kind === k.key && <span className="disc" />}
              </button>
            ))}
          </div>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="tprice">What you get paid</label>
            <input id="tprice" className="mono" type="number" min={0} step="0.01" value={priceStr} onChange={(e) => setPriceStr(e.target.value)} />
          </div>
          <div className="field">
            <label>Per</label>
            <div>
              {(['guest', 'job'] as const).map((p) => (
                <button key={p} type="button" className={`reason ${per === p ? 'selected' : ''}`} onClick={() => setPer(p)}>
                  {p === 'guest' ? 'Per guest' : 'Per job'}
                  {per === p && <span className="disc" />}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="row" style={{ marginTop: 16 }}>
          <div className="field">
            <label htmlFor="tdur">Duration (hours, optional)</label>
            <input id="tdur" className="mono" type="number" min={0} step="0.5" value={durationStr} onChange={(e) => setDurationStr(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="tmax">Max guests (optional)</label>
            <input id="tmax" className="mono" type="number" min={1} step="1" value={maxStr} onChange={(e) => setMaxStr(e.target.value)} />
          </div>
        </div>
        {err && <p className="error-line" role="alert">{err}</p>}
      </Panel>

      <div className="form-foot">
        <button className="btn secondary" onClick={back} style={{ flex: 1 }} disabled={busy}>Cancel</button>
        <button className="btn" onClick={save} style={{ flex: 2 }} disabled={busy || !canSave}>
          {busy ? 'Saving…' : isNew ? 'Add trip' : 'Save trip'}
        </button>
      </div>
    </div>
  );
}
